import { useAction } from "@reatom/npm-react";
import {
  PriceDtoCurrencyEnum,
  ProductControllerSearchV2GendersEnum,
  ProductControllerSearchV2SourcesEnum,
} from "definitions/product-storage";
import { useEffect, useMemo, useCallback } from "react";
import {
  useQueryParams,
  StringParam,
  NumberParam,
  ArrayParam,
  withDefault,
} from "use-query-params";

import { filterAtom } from "../store/atoms/filterQuery";
import {
  parseBoolean,
  parseEnumArray,
  parseIdArray,
  parseString,
} from "../utils";
import { ProductFilters } from "../widgets/ProductFilters/ProductFilters";

const CURRENCY_VALUES = Object.values(PriceDtoCurrencyEnum);
const GENDER_VALUES = Object.values(ProductControllerSearchV2GendersEnum);
const SOURCE_VALUES = Object.values(ProductControllerSearchV2SourcesEnum);

const queryConfig = {
  search: StringParam,
  currency: withDefault(StringParam, PriceDtoCurrencyEnum.Rub),
  brandIds: ArrayParam,
  categoryIds: ArrayParam,
  priceFrom: NumberParam,
  priceTo: NumberParam,
  viewAllSelected: StringParam,
  genders: ArrayParam,
  sources: ArrayParam,
  includeCustom: StringParam,
  excludeSamples: ArrayParam,
};

interface RawQueryFilters {
  search?: string | null;
  currency?: string | null;
  brandIds?: (string | null)[] | null;
  categoryIds?: (string | null)[] | null;
  priceFrom?: number | null;
  priceTo?: number | null;
  viewAllSelected?: string | null;
  genders?: (string | null)[] | null;
  sources?: (string | null)[] | null;
  includeCustom?: string | null;
  excludeSamples?: (string | null)[] | null;
}

const useFiltersFromQuery = (query: RawQueryFilters): ProductFilters => {
  return useMemo(() => {
    const currency =
      query.currency &&
      CURRENCY_VALUES.includes(query.currency as PriceDtoCurrencyEnum)
        ? (query.currency as PriceDtoCurrencyEnum)
        : PriceDtoCurrencyEnum.Rub;

    return {
      search: parseString(query.search),
      currency,
      brandIds: parseIdArray(query.brandIds),
      categoryIds: parseIdArray(query.categoryIds),
      priceFrom: query.priceFrom ?? undefined,
      priceTo: query.priceTo ?? undefined,
      viewAllSelected: parseBoolean(query.viewAllSelected),
      includeCustom: parseBoolean(query.includeCustom),
      genders: parseEnumArray(query.genders, GENDER_VALUES),
      sources: parseEnumArray(query.sources, SOURCE_VALUES),
      excludeSamples: parseIdArray(query.excludeSamples),
    };
  }, [query]);
};

export const useProductFiltersSync = (): void => {
  const setFilter = useAction(filterAtom);
  const [query] = useQueryParams(queryConfig);

  const filters = useFiltersFromQuery(query);

  const stableSetFilter = useCallback(
    (filters: ProductFilters) => {
      setFilter(filters);
    },
    [setFilter],
  );

  useEffect(() => {
    stableSetFilter(filters);
  }, [filters, stableSetFilter]);
};
